/**
 * UIComponentsProfilerTab - Покращена вкладка з об'єднаним профайлером
 * 
 * Особливості UI/UX:
 * - Сегментований фільтр для швидкого перемикання між типами
 * - Візуальні індикатори продуктивності (кольорові маркери)
 * - Компактні картки статистики
 * - Розумна таблиця з пошуком та фільтрами
 * - Експорт даних одним кліком
 * - Tree view ієрархії з підсвічуванням зв'язків
 * - Розширений пошук (regex, props, states, events)
 */

import {
    AppstoreOutlined,
    ClearOutlined,
    ClockCircleOutlined,
    CopyOutlined,
    DatabaseOutlined,
    DownloadOutlined,
    ReloadOutlined,
    SearchOutlined,
    ThunderboltOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons'
import {
    Badge,
    Button,
    Card,
    Col,
    Collapse,
    Descriptions,
    Input,
    message,
    Radio,
    Row,
    Segmented,
    Select,
    Space,
    Statistic,
    Table,
    Tabs,
    Tag,
    Timeline,
    Tooltip,
    Typography,
} from 'antd'
import { observer } from 'mobx-react-lite'
import React, { useMemo, useState } from 'react'
import { profilerManager } from '../../../Managers/Profiler'
import type { ProfilerComponentInfo } from '../../../Managers/Profiler/ProfilerManager'
import { AdvancedSearch } from './AdvancedSearch'
import { ComponentTreeView } from './ComponentTreeView'

const { Text, Title } = Typography
const { TextArea } = Input

type FilterType = 'all' | 'component' | 'template'
type PerformanceFilter = 'all' | 'fast' | 'medium' | 'slow'
type ViewMode = 'table' | 'tree'

export const UIComponentsProfilerTab: React.FC = observer(() => {
    const [searchText, setSearchText] = useState('')
    const [typeFilter, setTypeFilter] = useState<FilterType>('all')
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
    const [performanceFilter, setPerformanceFilter] = useState<PerformanceFilter>('all')
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
    const [viewMode, setViewMode] = useState<ViewMode>('table')
    const [selectedComponentId, setSelectedComponentId] = useState<string | undefined>(undefined)
    const [advancedFilteredComponents, setAdvancedFilteredComponents] = useState<ProfilerComponentInfo[]>([])
    const [useAdvancedSearch, setUseAdvancedSearch] = useState(false)

    /** Отримуємо всі компоненти */
    const allComponents = profilerManager.getAllComponents()

    /** Базова фільтрація (застосовується завжди) */
    const baseFilteredComponents = useMemo(() => {
        let filtered = useAdvancedSearch ? advancedFilteredComponents : allComponents

        return filtered.filter((comp) => {
            // Фільтр за типом
            if (typeFilter !== 'all' && comp.type !== typeFilter) return false

            // Фільтр за пошуком (тільки якщо не використовується розширений пошук)
            if (!useAdvancedSearch && searchText) {
                const search = searchText.toLowerCase()
                const matchesSearch =
                    comp.name.toLowerCase().includes(search) ||
                    comp.id.toLowerCase().includes(search) ||
                    comp.path.toLowerCase().includes(search)
                if (!matchesSearch) return false
            }

            // Фільтр за статусом
            if (statusFilter && comp.status !== statusFilter) return false

            // Фільтр за продуктивністю
            if (performanceFilter !== 'all') {
                const time = comp.averageRenderTime
                if (performanceFilter === 'fast' && time >= 20) return false
                if (performanceFilter === 'medium' && (time < 20 || time >= 50)) return false
                if (performanceFilter === 'slow' && time < 50) return false
            }

            return true
        })
    }, [
        allComponents,
        advancedFilteredComponents,
        useAdvancedSearch,
        typeFilter,
        searchText,
        statusFilter,
        performanceFilter,
    ])

    /** Фільтровані компоненти для відображення */
    const filteredComponents = baseFilteredComponents

    /** Статистика */
    const stats = profilerManager.getStats()
    const slowest = profilerManager.getSlowestComponents(1)[0]
    const memoryHeavy = profilerManager.getMemoryHeavyComponents(1)[0]
    const mostActive = profilerManager.getMostActiveComponents(1)[0]

    /** Копіювання */
    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text)
        message.success(`${label} скопійовано`)
    }

    /** Експорт */
    const handleExport = () => {
        const json = profilerManager.exportToJSON()
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `profiler-${Date.now()}.json`
        a.click()
        message.success('Дані експортовано')
    }

    /** Очищення історії */
    const handleCleanup = () => {
        profilerManager.cleanupEventHistory(50)
        message.success('Історію подій очищено')
    }

    /** Індикатор продуктивності */
    const PerformanceIndicator = ({ time }: { time: number }) => {
        const level = time > 100 ? 'critical' : time > 50 ? 'warning' : 'good'
        const color = level === 'critical' ? '#ff4d4f' : level === 'warning' ? '#faad14' : '#52c41a'
        const icon = level === 'critical' ? '🔴' : level === 'warning' ? '🟡' : '🟢'
        
        return (
            <Tooltip title={`${time.toFixed(2)} ms - ${level === 'critical' ? 'Критично повільно' : level === 'warning' ? 'Повільно' : 'Швидко'}`}>
                <Space size={4}>
                    <span>{icon}</span>
                    <Text style={{ color }}>{time.toFixed(1)} ms</Text>
                </Space>
            </Tooltip>
        )
    }

    const columns = [
        {
            title: 'Тип',
            dataIndex: 'type',
            key: 'type',
            width: 100,
            filters: [
                { text: 'Компонент', value: 'component' },
                { text: 'Шаблон', value: 'template' },
            ],
            onFilter: (value: any, record: ProfilerComponentInfo) => record.type === value,
            render: (type: string) => (
                <Tag color={type === 'component' ? 'blue' : 'purple'}>
                    {type === 'component' ? 'Component' : 'Template'}
                </Tag>
            ),
        },
        {
            title: 'Назва',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            sorter: (a: ProfilerComponentInfo, b: ProfilerComponentInfo) =>
                a.name.localeCompare(b.name),
            render: (name: string, record: ProfilerComponentInfo) => (
                <Space>
                    <Text strong>{name}</Text>
                    <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                            e.stopPropagation()
                            copyToClipboard(record.id, 'ID')
                        }}
                    />
                </Space>
            ),
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            filters: [
                { text: 'Активний', value: 'active' },
                { text: 'Неактивний', value: 'inactive' },
                { text: 'Помилка', value: 'error' },
            ],
            onFilter: (value: any, record: ProfilerComponentInfo) => record.status === value,
            render: (status: string) => (
                <Tag
                    icon={
                        status === 'active' ? '✓' : status === 'inactive' ? '⏸' : '✗'
                    }
                    color={
                        status === 'active' ? 'success' : status === 'inactive' ? 'warning' : 'error'
                    }
                >
                    {status === 'active' ? 'Активний' : status === 'inactive' ? 'Неактивний' : 'Помилка'}
                </Tag>
            ),
        },
        {
            title: 'Продуктивність',
            dataIndex: 'averageRenderTime',
            key: 'performance',
            width: 150,
            sorter: (a: ProfilerComponentInfo, b: ProfilerComponentInfo) =>
                a.averageRenderTime - b.averageRenderTime,
            render: (time: number) => <PerformanceIndicator time={time} />,
        },
        {
            title: 'Рендерів',
            dataIndex: 'renderCount',
            key: 'renderCount',
            width: 100,
            align: 'right' as const,
            sorter: (a: ProfilerComponentInfo, b: ProfilerComponentInfo) =>
                a.renderCount - b.renderCount,
            render: (count: number) => <Text>{count}</Text>,
        },
        {
            title: 'Пам\'ять',
            dataIndex: 'memoryUsage',
            key: 'memory',
            width: 110,
            sorter: (a: ProfilerComponentInfo, b: ProfilerComponentInfo) =>
                a.memoryUsage - b.memoryUsage,
            align: 'right' as const,
            render: (memory: number) => (
                <Text>{(memory / 1024).toFixed(1)} KB</Text>
            ),
        },
        {
            title: 'Подій',
            dataIndex: 'eventHistory',
            key: 'events',
            width: 90,
            sorter: (a: ProfilerComponentInfo, b: ProfilerComponentInfo) =>
                a.eventHistory.length - b.eventHistory.length,
            align: 'right' as const,
            render: (events: any[]) => <Badge count={events.length} />,
        },
        {
            title: 'Шлях',
            dataIndex: 'path',
            key: 'path',
            ellipsis: true,
            render: (path: string) => (
                <Tooltip title={path}>
                    <Text code style={{ fontSize: '11px' }}>{path}</Text>
                </Tooltip>
            ),
        },
    ]

    /** Розгорнутий перегляд - без вкладених таблиць */
    const expandedRowRender = (record: ProfilerComponentInfo) => {
        // Явно вказуємо, що це НЕ Table компонент
        return (
            <div 
                style={{ padding: '12px 0', background: '#fafafa' }}
                data-no-table="true"
            >
                <Tabs
                    size="small"
                    type="card"
                    items={[
                        {
                            key: 'overview',
                            label: 'Огляд',
                            children: (
                                <Descriptions bordered size="small" column={3}>
                                <Descriptions.Item label="ID" span={3}>
                                    <Space>
                                        <Text code>{record.id}</Text>
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<CopyOutlined />}
                                            onClick={() => copyToClipboard(record.id, 'ID')}
                                        />
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Тип">{record.type}</Descriptions.Item>
                                <Descriptions.Item label="Статус">{record.status}</Descriptions.Item>
                                <Descriptions.Item label="Батьківський ID">
                                    {record.parentId || 'Немає'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Дочірніх">{record.children.length}</Descriptions.Item>
                                <Descriptions.Item label="Створено">
                                    {new Date(record.createdAt).toLocaleString()}
                                </Descriptions.Item>
                                <Descriptions.Item label="Оновлено">
                                    {new Date(record.updatedAt).toLocaleString()}
                                </Descriptions.Item>
                                <Descriptions.Item label="Рендерів">{record.renderCount}</Descriptions.Item>
                                <Descriptions.Item label="Останній рендер">
                                    {record.lastRenderTime.toFixed(2)} ms
                                </Descriptions.Item>
                                <Descriptions.Item label="Середній рендер">
                                    {record.averageRenderTime.toFixed(2)} ms
                                </Descriptions.Item>
                                <Descriptions.Item label="Загальний час">
                                    {record.totalRenderTime.toFixed(2)} ms
                                </Descriptions.Item>
                                <Descriptions.Item label="Пам\'ять">
                                    {(record.memoryUsage / 1024).toFixed(2)} KB
                                </Descriptions.Item>
                                <Descriptions.Item label="Обробників подій">
                                    {record.eventHandlers.length}
                                </Descriptions.Item>
                                {record.errorMessage && (
                                    <Descriptions.Item label="Помилка" span={3}>
                                        <Text type="danger">{record.errorMessage}</Text>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        ),
                    },
                    {
                        key: 'data',
                        label: `Дані (${Object.keys(record.states).length + Object.keys(record.storages).length + Object.keys(record.settings).length + Object.keys(record.props).length})`,
                        children: (
                            <Collapse
                                size="small"
                                items={[
                                    {
                                        key: 'states',
                                        label: `States (${Object.keys(record.states).length})`,
                                        children: (
                                            <TextArea
                                                value={JSON.stringify(record.states, null, 2)}
                                                readOnly
                                                autoSize={{ minRows: 3, maxRows: 15 }}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'storages',
                                        label: `Storages (${Object.keys(record.storages).length})`,
                                        children: (
                                            <TextArea
                                                value={JSON.stringify(record.storages, null, 2)}
                                                readOnly
                                                autoSize={{ minRows: 3, maxRows: 15 }}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'settings',
                                        label: `Settings (${Object.keys(record.settings).length})`,
                                        children: (
                                            <TextArea
                                                value={JSON.stringify(record.settings, null, 2)}
                                                readOnly
                                                autoSize={{ minRows: 3, maxRows: 15 }}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'props',
                                        label: `Props (${Object.keys(record.props).length})`,
                                        children: (
                                            <TextArea
                                                value={JSON.stringify(record.props, null, 2)}
                                                readOnly
                                                autoSize={{ minRows: 3, maxRows: 15 }}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        ),
                    },
                    {
                        key: 'events',
                        label: `Події (${record.eventHistory.length})`,
                        children: (
                            <Timeline
                                mode="left"
                                items={record.eventHistory
                                    .slice(-15)
                                    .reverse()
                                    .map((event) => ({
                                        color: event.event.includes('error') ? 'red' : 'blue',
                                        children: (
                                            <div>
                                                <Text strong>{event.event}</Text>
                                                <br />
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {new Date(event.timestamp).toLocaleString()}
                                                </Text>
                                                {event.data && (
                                                    <>
                                                        <br />
                                                        <Text code style={{ fontSize: '11px' }}>
                                                            {JSON.stringify(event.data)}
                                                        </Text>
                                                    </>
                                                )}
                                            </div>
                                        ),
                                    }))}
                            />
                        ),
                    },
                ]}
            />
            </div>
        )
    }

    return (
        <div>
            {/* Перемикач режиму перегляду */}
            <Card size="small" style={{ marginBottom: 16 }}>
                <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                    <Space wrap align="center">
                        <Text strong>Режим перегляду:</Text>
                        <Radio.Group
                            value={viewMode}
                            onChange={(e) => setViewMode(e.target.value)}
                            buttonStyle="solid"
                            size="small"
                        >
                            <Radio.Button value="table">
                                <UnorderedListOutlined /> Таблиця
                            </Radio.Button>
                            <Radio.Button value="tree">
                                <AppstoreOutlined /> Дерево
                            </Radio.Button>
                        </Radio.Group>
                    </Space>
                </Space>
            </Card>

            {/* Розширений пошук */}
            <div style={{ marginBottom: 16 }}>
                <AdvancedSearch
                    components={allComponents}
                    onFilterChange={(filtered) => {
                        setAdvancedFilteredComponents(filtered)
                        setUseAdvancedSearch(filtered.length !== allComponents.length || filtered.length === 0)
                    }}
                />
            </div>

            {/* Компактні картки статистики */}
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title="Всього компонентів"
                            value={stats.components.total}
                            prefix={<ThunderboltOutlined />}
                            styles={{ content: { fontSize: '24px' } }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title="Активних"
                            value={stats.components.active}
                            styles={{ content: { color: '#52c41a', fontSize: '24px' } }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title="З помилками"
                            value={stats.components.errors}
                            styles={{ content: { color: '#ff4d4f', fontSize: '24px' } }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title="Середній рендер"
                            value={stats.performance.averageRenderTime.toFixed(2)}
                            suffix="ms"
                            prefix={<ClockCircleOutlined />}
                            styles={{ content: { fontSize: '24px' } }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title="Пам'ять"
                            value={(stats.performance.totalMemory / 1024 / 1024).toFixed(2)}
                            suffix="MB"
                            prefix={<DatabaseOutlined />}
                            styles={{ content: { fontSize: '24px' } }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Card size="small">
                        <Tooltip title={slowest?.name}>
                            <Statistic
                                title="Найповільніший"
                                value={slowest?.averageRenderTime.toFixed(2) || '0.00'}
                                suffix="ms"
                                styles={{
                                    content: {
                                        fontSize: '24px',
                                        color: (slowest?.averageRenderTime || 0) > 50 ? '#ff4d4f' : undefined,
                                    },
                                }}
                            />
                        </Tooltip>
                    </Card>
                </Col>
            </Row>

            {/* Панель фільтрів */}
            <Card size="small" style={{ marginBottom: 16 }}>
                <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                    {/* Сегментований фільтр */}
                    <Space wrap align="center">
                        <Text strong>Тип:</Text>
                        <Segmented
                            value={typeFilter}
                            onChange={(value) => setTypeFilter(value as FilterType)}
                            options={[
                                { label: 'Всі', value: 'all' },
                                { label: 'Компоненти', value: 'component' },
                                { label: 'Шаблони', value: 'template' },
                            ]}
                        />
                    </Space>

                    {/* Пошук та фільтри */}
                    <Space wrap>
                        <Input
                            placeholder="Пошук за назвою, ID або шляхом..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: 300 }}
                            allowClear
                        />
                        <Select
                            placeholder="Статус"
                            value={statusFilter}
                            onChange={setStatusFilter}
                            style={{ width: 150 }}
                            allowClear
                        >
                            <Select.Option value="active">Активні</Select.Option>
                            <Select.Option value="inactive">Неактивні</Select.Option>
                            <Select.Option value="error">З помилками</Select.Option>
                        </Select>
                        <Select
                            placeholder="Продуктивність"
                            value={performanceFilter}
                            onChange={setPerformanceFilter}
                            style={{ width: 180 }}
                        >
                            <Select.Option value="all">Всі</Select.Option>
                            <Select.Option value="fast">Швидкі (&lt;20ms)</Select.Option>
                            <Select.Option value="medium">Середні (20-50ms)</Select.Option>
                            <Select.Option value="slow">Повільні (&gt;50ms)</Select.Option>
                        </Select>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                setSearchText('')
                                setTypeFilter('all')
                                setStatusFilter(undefined)
                                setPerformanceFilter('all')
                            }}
                        >
                            Скинути
                        </Button>
                        <Button icon={<DownloadOutlined />} onClick={handleExport}>
                            Експорт
                        </Button>
                        <Button icon={<ClearOutlined />} onClick={handleCleanup}>
                            Очистити історію
                        </Button>
                    </Space>

                    <Text type="secondary">
                        Знайдено: {filteredComponents.length} з {allComponents.length} компонентів
                    </Text>
                </Space>
            </Card>

            {/* Таблиця або Дерево */}
            {viewMode === 'table' ? (
                <Table
                    columns={columns}
                    dataSource={filteredComponents}
                    rowKey="id"
                    size="small"
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} з ${total}`,
                    }}
                    expandable={{
                        expandedRowRender,
                        expandedRowKeys,
                        onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
                        rowExpandable: () => true,
                        expandRowByClick: false,
                        // Явно вказуємо, що не використовуємо вкладені таблиці
                        childrenColumnName: '__children__', // Унікальне ім'я, щоб уникнути конфлікту
                        indentSize: 0, // Вимикаємо індентацію для дерева
                    }}
                />
            ) : (
                <Card size="small">
                    <ComponentTreeView
                        components={filteredComponents}
                        selectedComponentId={selectedComponentId}
                        onSelectComponent={(id) => {
                            setSelectedComponentId(id)
                            // Автоматично розгортаємо деталі в таблиці при виборі
                            setExpandedRowKeys([id])
                        }}
                    />
                </Card>
            )}
        </div>
    )
})
